// @givmo/employer-module — the company deposit's charge series, held beyond the dialog for the
// browser session (CAM-R51, widened by D-EP-53). ★ MONEY ★
//
// WHY. The backend de-duplicates a company deposit by its Idempotency-Key alone (`claim_idempotency`
// on `POST /admin/company/wallet/deposit`, and the durable charge intent under the same key), so the
// key is the only thing between a re-entered deposit and a second charge. The dialog used to hold its
// series in its own memory, and three ordinary moves unmounted it — a Settings tab switch in the
// portal (`Tabs.Panel` keeps no inactive panel mounted), leaving the console's Wallet, a reload — so
// the next deposit went out under a new key while the maybe-charged close had promised a re-entry
// "will not double-charge" (the cut-15 review's P1). This module holds every unresolved series beyond
// the dialog: the key AND the exact request it went out with (CAM-R59b's frozen request), so
// re-entering that deposit resends the same bytes under the same key and the backend replays.
//
// WHERE IT LIVES: module memory above the router, written through to `sessionStorage`.
//   - Memory survives the dialog's and the page's unmount for the life of the document: a tab
//     switch, a navigation away and back.
//   - `sessionStorage` survives a reload, which memory cannot. It is per tab and per origin (the
//     portal and the console are different origins), and a tab opened from this one starts with a
//     copy. So a deposit re-entered from another tab, from the other host or from another device
//     goes out under a new key — which is why the maybe-charged close says a re-entry is safe "in
//     this tab" — and the server's duplicate confirm (EP-BE15) is the guard there.
//   - Storage can be missing or throw (a private window, blocked site data): every read and write is
//     guarded, and memory alone still covers the unmount cases.
//
// SCOPE: the signed-in person, and their company when it is known. The backend fingerprints a keyed
// request with the caller's Firebase uid (`idempotency_scope_for` → `uid:<uid>`) and charges the
// caller's own company (`user.admin_company`): a key resent by another person is refused as reused
// and never converges, and one resent for another company would replay that company's answer. So the
// tab holds ONE shelf per uid — named from the auth context, which every signed-in page has, never
// from a read that can fail (cut 19's review F3: a host whose admin read failed after a reload used to
// mint a new key for the deposit in doubt). Each series records the company it went out for (from
// the host's admin read or its wallet read); while the company is known, a series another company's
// is never replayed, and while it is not, every series of this uid still is: a signature the tab
// holds is never re-keyed. Only a host that cannot name the uid at all holds the series in the
// dialog's own memory, and hands it over once it can (`adoptDepositSeries`).
//
// PER SIGNATURE: every unresolved series is held under its signature (amount + card + cover-fees
// choice, `depositFingerprint`), so a re-entry replays even after another deposit in between.
//
// CLEARED on a definite outcome (`settle`): a confirmed success, or a refusal that charged nothing.
// BOUNDED in time: a series is forgotten 24 hours after its first charge went out. The backend
// replays a completed request for 24 hours (`IDEMPOTENCY_RETENTION_SECONDS`) and Shift4 binds a key
// for at least as long, so the browser never holds a key past the window the server replays it in.

import type { DepositFunds } from '@givmo/api-client';
import type { DisclosureEchoFields, ResolvedDisclosure } from '@givmo/disclosure';
import { uuidV4 } from '@givmo/payments';
import { depositFingerprint, type DepositSignature } from './deposit-idempotency';
import type { DepositQuote, DepositQuoteBreakdown } from './wallet-decode';

/** Whose deposits a series belongs to: the signed-in person, and the company when it is known. */
export interface DepositSeriesScope {
  /** The signed-in person's Firebase uid: the principal the backend fingerprints a key with. */
  readonly uid: string;
  /**
   * The company the deposit is credited to (`AdminOut.admin_company`, or the wallet read's
   * `domain_id`), or `null` while neither read has answered.
   */
  readonly companyId: string | null;
}

/**
 * The scope a host names: the signed-in uid (its auth context) and the company (its admin read, else
 * its wallet read). `null` only while there is no uid. Never throws.
 */
export function depositSeriesScope(uid: unknown, companyId: unknown): DepositSeriesScope | null {
  if (typeof uid !== 'string' || uid.length === 0) return null;
  return {
    uid,
    companyId: typeof companyId === 'string' && companyId.length > 0 ? companyId : null,
  };
}

/**
 * `DepositFunds` plus the company deposit's optional stated gross, disclosure echo and duplicate
 * confirm (EP-BE15), ahead of a snapshot regen (`agent_docs/api-client.md` § Additive request fields
 * ahead of a regen). `duplicate_check` and `confirm_duplicate` are literals: the dialog only ever
 * asks for the check, and only ever confirms what the person confirmed.
 */
export type CompanyDepositBody = DepositFunds & {
  expected_amount_charged?: string;
  duplicate_check?: 'prompt';
  confirm_duplicate?: true;
} & DisclosureEchoFields;

/**
 * One deposit request exactly as sent, frozen when its charge starts (review F2): the wire body
 * itself, and what the dialog stated it with. A retry, or the same deposit re-entered after a
 * maybe-charged close, resends THIS body, never one rebuilt from state that may have moved since
 * (the quote can re-read while a charge is in doubt).
 */
export interface DepositRequest {
  /** The wire amount, e.g. "20.00". */
  readonly amount: string;
  readonly cardId: string;
  readonly coverFees: boolean;
  /** The gross the dialog stated; the backend refuses to charge any other. */
  readonly expectedAmountCharged: string;
  /** The confirm sentence this request was stated under. */
  readonly summary: string;
  /** The quote the request was stated from (the fee line it was stated beside). */
  readonly quote: DepositQuote;
  /** The company deposit body the request was stated under; its id + hash ride in `body`. */
  readonly disclosure: ResolvedDisclosure;
  /** The exact JSON body the charge goes out with. */
  readonly body: CompanyDepositBody;
}

/** A held series: the key a deposit went out under, the request it went out with, and when. */
export interface DepositSeriesEntry {
  readonly key: string;
  readonly request: DepositRequest;
  /** The company it went out for, or `null` when the host could not yet name it. */
  readonly companyId: string | null;
  /** When the series' first charge went out (epoch ms); the 24-hour bound counts from here. */
  readonly startedAt: number;
  /** When it last went out (epoch ms); the most recent is the one a reopened dialog starts from. */
  readonly attemptedAt: number;
}

/** How long a series is held after its first charge went out: the backend's replay window. */
export const DEPOSIT_SERIES_TTL_MS = 24 * 60 * 60 * 1000;

/** The unresolved deposit series of one scope. */
export interface DepositSeries {
  /** The series held for this signature, or `null`. */
  heldFor(signature: DepositSignature): DepositSeriesEntry | null;
  /** The series that went out most recently, or `null` when none is held. */
  latest(): DepositSeriesEntry | null;
  /**
   * The series a NEW charge of this request goes out in: the one already held for its signature
   * (whose own request then goes out — one key, one body), or a fresh key held from now on.
   */
  start(request: DepositRequest): DepositSeriesEntry;
  /** Hold this series again as it goes out once more ("Try again"): never a new key. */
  attempt(entry: DepositSeriesEntry): DepositSeriesEntry;
  /** A definite outcome: drop this series, unless another key has taken its signature since. */
  settle(entry: DepositSeriesEntry): void;
  /** Every series held, oldest first. */
  entries(): DepositSeriesEntry[];
}

type Shelf = Map<string, DepositSeriesEntry>;

const STORAGE_PREFIX = 'givmo.employer.deposit-series.v2';

/** Module memory: each scope's shelf, once read, for the life of the document. */
const shelves = new Map<string, Shelf>();

const listeners = new Set<() => void>();
let version = 0;

function changed(): void {
  version += 1;
  for (const listener of listeners) listener();
}

/** Subscribe to every change of every held series (`useSyncExternalStore`). */
export function subscribeDepositSeries(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A number that moves whenever a held series changes (`useSyncExternalStore`'s snapshot). */
export function depositSeriesVersion(): number {
  return version;
}

function signatureOf(request: DepositRequest): DepositSignature {
  return { amount: request.amount, cardId: request.cardId, coverFees: request.coverFees };
}

function fingerprintOf(request: DepositRequest): string {
  return depositFingerprint(signatureOf(request));
}

function storageKeyOf(scope: DepositSeriesScope): string {
  return `${STORAGE_PREFIX}:${scope.uid}`;
}

function sessionStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

// ── The stored form, read back tolerantly: anything malformed is dropped, never thrown ────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function decodeBreakdown(raw: unknown): DepositQuoteBreakdown | null {
  if (!isRecord(raw)) return null;
  const { amountCharged, processorFee, amountCredited, coverFees } = raw;
  return typeof amountCharged === 'string' &&
    typeof processorFee === 'string' &&
    typeof amountCredited === 'string' &&
    typeof coverFees === 'boolean'
    ? { amountCharged, processorFee, amountCredited, coverFees }
    : null;
}

function decodeQuote(raw: unknown): DepositQuote | null {
  if (!isRecord(raw) || typeof raw.companyCoversFees !== 'boolean') return null;
  const uncovered = decodeBreakdown(raw.uncovered);
  const covered = decodeBreakdown(raw.covered);
  return uncovered === null || covered === null
    ? null
    : { companyCoversFees: raw.companyCoversFees, uncovered, covered };
}

function decodeDisclosure(raw: unknown): ResolvedDisclosure | null {
  if (!isRecord(raw)) return null;
  const { lines, contentHash, versionId, isFloor, appliesToState } = raw;
  if (!Array.isArray(lines) || !lines.every((line) => typeof line === 'string')) return null;
  if (typeof contentHash !== 'string' || typeof isFloor !== 'boolean') return null;
  if (versionId !== null && typeof versionId !== 'string') return null;
  if (appliesToState !== undefined && appliesToState !== null && typeof appliesToState !== 'string')
    return null;
  return {
    lines: lines as string[],
    contentHash,
    versionId,
    isFloor,
    ...(appliesToState === undefined ? {} : { appliesToState }),
  };
}

/**
 * Whether a stored body is the wire body of this deposit. It is kept as stored — every key, in its
 * order — because it goes out again byte for byte.
 */
function isBodyOf(
  body: Record<string, unknown>,
  signature: DepositSignature,
): body is Record<string, unknown> & CompanyDepositBody {
  return (
    body.amount === signature.amount &&
    body.card_id === signature.cardId &&
    body.cover_fees === signature.coverFees &&
    typeof body.payment_method === 'string'
  );
}

function decodeRequest(raw: unknown): DepositRequest | null {
  if (!isRecord(raw)) return null;
  const { amount, cardId, coverFees, expectedAmountCharged, summary, body } = raw;
  if (
    typeof amount !== 'string' ||
    typeof cardId !== 'string' ||
    typeof coverFees !== 'boolean' ||
    typeof expectedAmountCharged !== 'string' ||
    typeof summary !== 'string' ||
    !isRecord(body)
  ) {
    return null;
  }
  // The body goes out again byte for byte, so it must be the body of THIS request.
  if (!isBodyOf(body, { amount, cardId, coverFees })) return null;
  const quote = decodeQuote(raw.quote);
  const disclosure = decodeDisclosure(raw.disclosure);
  if (quote === null || disclosure === null) return null;
  return {
    amount,
    cardId,
    coverFees,
    expectedAmountCharged,
    summary,
    quote,
    disclosure,
    body,
  };
}

function decodeEntry(raw: unknown): DepositSeriesEntry | null {
  if (!isRecord(raw)) return null;
  const { key, startedAt, attemptedAt, companyId } = raw;
  if (typeof key !== 'string' || key.length === 0) return null;
  if (typeof startedAt !== 'number' || !Number.isFinite(startedAt)) return null;
  if (typeof attemptedAt !== 'number' || !Number.isFinite(attemptedAt)) return null;
  if (companyId !== null && (typeof companyId !== 'string' || companyId.length === 0)) return null;
  const request = decodeRequest(raw.request);
  return request === null ? null : { key, request, companyId, startedAt, attemptedAt };
}

function readStored(storageKey: string): Shelf {
  const shelf: Shelf = new Map();
  try {
    const raw = sessionStore()?.getItem(storageKey) ?? null;
    if (raw === null) return shelf;
    const stored: unknown = JSON.parse(raw);
    if (!Array.isArray(stored)) return shelf;
    for (const item of stored) {
      const entry = decodeEntry(item);
      if (entry !== null) shelf.set(fingerprintOf(entry.request), entry);
    }
  } catch {
    // Unreadable or unavailable storage holds nothing; memory carries the document's own series.
  }
  return shelf;
}

function writeStored(storageKey: string, shelf: Shelf): void {
  try {
    const storage = sessionStore();
    if (storage === null) return;
    if (shelf.size === 0) storage.removeItem(storageKey);
    else storage.setItem(storageKey, JSON.stringify([...shelf.values()]));
  } catch {
    // A browser that keeps nothing (private window, blocked site data, a full quota) loses the
    // series on reload; memory still holds it for this document.
  }
}

// ── The series over one shelf ─────────────────────────────────────────────────────────────────────

class ShelfSeries implements DepositSeries {
  readonly #load: () => Shelf;
  readonly #save: (shelf: Shelf) => void;
  readonly #companyId: string | null;

  constructor(load: () => Shelf, save: (shelf: Shelf) => void, companyId: string | null = null) {
    this.#load = load;
    this.#save = save;
    this.#companyId = companyId;
  }

  /**
   * Whether a held series is this company's to replay: always while the company is unknown (a
   * signature the tab holds is never re-keyed), and otherwise unless it went out for ANOTHER company
   * (the signed-in person moved: replaying that key would answer the other company's deposit).
   */
  #ours(entry: DepositSeriesEntry): boolean {
    return (
      this.#companyId === null || entry.companyId === null || entry.companyId === this.#companyId
    );
  }

  /** The shelf with every series past the bound dropped (and the drop written through). */
  #live(): Shelf {
    const shelf = this.#load();
    const now = Date.now();
    let pruned = false;
    for (const [fingerprint, entry] of shelf) {
      if (now - entry.startedAt >= DEPOSIT_SERIES_TTL_MS) {
        shelf.delete(fingerprint);
        pruned = true;
      }
    }
    if (pruned) this.#save(shelf);
    return shelf;
  }

  heldFor(signature: DepositSignature): DepositSeriesEntry | null {
    const held = this.#live().get(depositFingerprint(signature));
    return held !== undefined && this.#ours(held) ? held : null;
  }

  latest(): DepositSeriesEntry | null {
    let latest: DepositSeriesEntry | null = null;
    for (const entry of this.#live().values()) {
      if (!this.#ours(entry)) continue;
      if (latest === null || entry.attemptedAt >= latest.attemptedAt) latest = entry;
    }
    return latest;
  }

  start(request: DepositRequest): DepositSeriesEntry {
    const held = this.#live().get(fingerprintOf(request));
    if (held !== undefined && this.#ours(held)) return this.attempt(held);
    // A fresh key — replacing, for this signature, only a series another company's.
    const now = Date.now();
    return this.#hold({
      key: uuidV4(),
      request,
      companyId: this.#companyId,
      startedAt: now,
      attemptedAt: now,
    });
  }

  attempt(entry: DepositSeriesEntry): DepositSeriesEntry {
    // A series sent while the company was unknown learns it the next time it goes out.
    return this.#hold({
      ...entry,
      companyId: entry.companyId ?? this.#companyId,
      attemptedAt: Date.now(),
    });
  }

  settle(entry: DepositSeriesEntry): void {
    const shelf = this.#live();
    const fingerprint = fingerprintOf(entry.request);
    if (shelf.get(fingerprint)?.key !== entry.key) return;
    shelf.delete(fingerprint);
    this.#save(shelf);
    changed();
  }

  entries(): DepositSeriesEntry[] {
    return [...this.#live().values()]
      .filter((entry) => this.#ours(entry))
      .sort((a, b) => a.startedAt - b.startedAt);
  }

  #hold(entry: DepositSeriesEntry): DepositSeriesEntry {
    // Not `#live()`: a series sent again ("Try again") is held even past the bound, so its key is
    // never re-minted while it is in doubt.
    const shelf = this.#load();
    shelf.set(fingerprintOf(entry.request), entry);
    this.#save(shelf);
    changed();
    return entry;
  }
}

/**
 * The series held for the signed-in person (one shelf per uid: module memory, written through to
 * `sessionStorage`), seen as their company's while the company is known.
 */
export function sessionDepositSeries(scope: DepositSeriesScope): DepositSeries {
  const storageKey = storageKeyOf(scope);
  return new ShelfSeries(
    () => {
      let shelf = shelves.get(storageKey);
      if (shelf === undefined) {
        shelf = readStored(storageKey);
        shelves.set(storageKey, shelf);
      }
      return shelf;
    },
    (shelf) => writeStored(storageKey, shelf),
    scope.companyId,
  );
}

/** A series held in one dialog's memory only — for a host that cannot name the uid yet. */
export function memoryDepositSeries(): DepositSeries {
  const shelf: Shelf = new Map();
  return new ShelfSeries(
    () => shelf,
    () => {},
  );
}

/** Hand every series `from` holds over to `into` (the scope became known), keys unchanged. */
export function adoptDepositSeries(from: DepositSeries, into: DepositSeries): void {
  for (const entry of from.entries()) {
    into.attempt(entry);
    from.settle(entry);
  }
}

/**
 * Forget every held series, as a fresh document would. `keepStorage` keeps what `sessionStorage`
 * holds, so the next read starts from it exactly as after a reload. Tests only.
 */
export function resetDepositSeriesForTests(options: { keepStorage?: boolean } = {}): void {
  shelves.clear();
  if (options.keepStorage === true) return;
  try {
    const storage = sessionStore();
    if (storage === null) return;
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key !== null && key.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
    // Nothing to clear.
  }
}
